import { PureComponent } from 'react';
import * as React from 'react';
// Material helpers
import { withStyles, Button, Typography } from '@material-ui/core';
// DateTime helpers
import { DateTime } from 'luxon';
import moment from 'moment';
// Snackbar notifications
import { withSnackbar } from 'notistack';
// Component styles
import styles from './styles';
// Replace Calendar with SimplifiedDatePicker
import SimplifiedDatePicker from './SimplifiedDatePicker';
import { TabContainer } from 'components';

class SearchToolbar extends PureComponent {
  constructor(props) {
    super(props);

    // Set the minimum and maximum dates
    this.range = {
      min: DateTime.fromObject({ hour: 0 }),
      max: DateTime.fromObject({ hour: 0 }).plus({ days: 6 })
    };

    // Try to restore saved search state from localStorage
    const savedState = this.loadSearchState();

    // Initialize the component's state
    this.state = {
      start: savedState.start || this.range.min,
      end:
        savedState.end ||
        this.range.min.plus({ hours: 24 }).minus({ seconds: 1 }),
      selectedTime: savedState.selectedTime || 'anytime',
      value: [null, null],
      activeTab:
        savedState.activeTab !== undefined
          ? savedState.activeTab
          : props.initialTab !== undefined
          ? props.initialTab
          : 0
    };

    // Bind state-manipulating functions
    this.handleSearchClick = this.handleSearchClick.bind(this);
    this.handleGroupSearchClick = this.handleGroupSearchClick.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleTimeChange = this.handleTimeChange.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
  }

  // Load saved search state from localStorage
  loadSearchState() {
    try {
      const saved = localStorage.getItem('bookingSearchState');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert saved timestamps back to DateTime objects
        if (parsed.start) {
          parsed.start = DateTime.fromMillis(parsed.start);
        }
        if (parsed.end) {
          parsed.end = DateTime.fromMillis(parsed.end);
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error loading search state from localStorage:', error);
    }
    return {};
  }

  // Save search state to localStorage
  saveSearchState(state, activeTab) {
    try {
      const stateToSave = {
        start: state.start?.toMillis ? state.start.toMillis() : state.start,
        end: state.end?.toMillis ? state.end.toMillis() : state.end,
        selectedTime: state.selectedTime,
        activeTab: activeTab !== undefined ? activeTab : this.state.activeTab
      };
      localStorage.setItem('bookingSearchState', JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Error saving search state to localStorage:', error);
    }
  }

  componentDidMount() {
    // If we have saved state and it's valid, auto-trigger the search
    // Use the state that was already loaded in constructor to avoid duplicate loading
    if (
      this.state.start &&
      this.state.end &&
      (this.state.start !== this.range.min ||
        this.state.end !==
          this.range.min.plus({ hours: 24 }).minus({ seconds: 1 }))
    ) {
      // Small delay to ensure all components are mounted
      setTimeout(() => {
        const { onSearch, onGroupSearch } = this.props;
        const activeTab = this.state.activeTab;

        // Build search query from current state (convert DateTime to milliseconds)
        const searchQuery = {
          start: this.state.start.toMillis(),
          end: this.state.end.toMillis(),
          selectedTime: this.state.selectedTime || 'anytime'
        };

        // Trigger search based on active tab
        if (activeTab === 0 && onSearch) {
          onSearch(searchQuery);
        } else if (activeTab === 1 && onGroupSearch) {
          onGroupSearch(searchQuery);
        }
      }, 100);
    }
  }

  handleSearchClick() {
    const { onSearch } = this.props;
    const searchQuery = {};

    Object.entries(this.state).forEach(([key, value]) => {
      if (value !== null) {
        if (['start', 'end'].includes(key)) {
          value = value.toMillis();
        }
        searchQuery[key] = value;
      }
    });

    // Save search state to localStorage
    this.saveSearchState(this.state, 0);

    onSearch(searchQuery);
  }

  handleGroupSearchClick() {
    const { onGroupSearch } = this.props;
    const searchQuery = {};

    // Customize the search query for group sessions if needed
    Object.entries(this.state).forEach(([key, value]) => {
      if (value !== null) {
        if (['start', 'end'].includes(key)) {
          value = value.toMillis();
        }
        searchQuery[key] = value;
      }
    });

    // Save search state to localStorage
    this.saveSearchState(this.state, 1);

    onGroupSearch(searchQuery);
  }

  handleTimeChange(event) {
    const timeValue = event.target.value;
    this.setState({ selectedTime: timeValue }, () => {
      // Save state when time changes
      this.saveSearchState(this.state, this.state.activeTab);
    });
  }

  handleDateChange(startDate, endDate) {
    // Ensure startDate is at start of day and endDate is at end of day
    const start = startDate.startOf('day');
    const end = endDate.endOf('day');

    this.setState(
      {
        start: start,
        end: end
      },
      () => {
        // Save state when dates change
        this.saveSearchState(this.state, this.state.activeTab);
      }
    );
  }

  handleTabChange(tabIndex) {
    this.setState({ activeTab: tabIndex }, () => {
      // Save state when tab changes
      this.saveSearchState(this.state, tabIndex);
    });

    // Call the parent's onTabChange if provided
    if (this.props.onTabChange) {
      this.props.onTabChange();
    }
  }

  render() {
    const {
      classes,
      loading,
      message,
      groupSessionMessage,
      renderOneOnOneSearch,
      renderGroupSearch,
      onTabChange,
      initialTab
    } = this.props;

    const tabs = [
      {
        label: 'One-on-One Lessons',
        component: (
          <>
            <div className={classes.root}>
              <Typography className={classes.title} variant="h2">
                Booking Search
              </Typography>
              <Typography className={classes.subtitle} variant="subtitle1">
                Search for a date and then available teachers and booking times
                will become visible.
                <br />
                After you choose a teacher, you will then choose your lesson
                topic.
              </Typography>
              <div
                className={classes.row}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '15px'
                }}>
                <SimplifiedDatePicker
                  onDateChange={this.handleDateChange}
                  selectedTime={this.state.selectedTime}
                  onTimeChange={this.handleTimeChange}
                  showTime={true}
                  isSearch={this.props.isSearch}
                  startDate={
                    this.state.start
                      ? moment(this.state.start.toJSDate())
                      : null
                  }
                  endDate={
                    this.state.end ? moment(this.state.end.toJSDate()) : null
                  }
                />
                <Button
                  className={classes.searchButton}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={this.handleSearchClick}
                  style={{ marginTop: '15px' }}>
                  Search
                </Button>
              </div>
              {message && (
                <Typography variant="h5" className={classes.errorMessage}>
                  {message}
                </Typography>
              )}
            </div>
            {renderOneOnOneSearch && renderOneOnOneSearch}
          </>
        )
      },
      {
        label: 'Group Sessions',
        component: (
          <>
            <div className={classes.root}>
              <Typography className={classes.title} variant="h2">
                Group Sessions Search
              </Typography>
              <Typography className={classes.subtitle} variant="subtitle1">
                Search for a date and then available teachers and booking times
                will become visible.
              </Typography>
              <div
                className={classes.row}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '15px'
                }}>
                <SimplifiedDatePicker
                  onDateChange={this.handleDateChange}
                  selectedTime={this.state.selectedTime}
                  onTimeChange={this.handleTimeChange}
                  showTime={true}
                  isSearch={this.props.isSearch}
                  startDate={
                    this.state.start
                      ? moment(this.state.start.toJSDate())
                      : null
                  }
                  endDate={
                    this.state.end ? moment(this.state.end.toJSDate()) : null
                  }
                />
                <Button
                  className={classes.searchButton}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={this.handleGroupSearchClick}
                  style={{ marginTop: '15px' }}>
                  Search
                </Button>
              </div>
              {groupSessionMessage && (
                <Typography variant="h5" className={classes.errorMessage}>
                  {groupSessionMessage}
                </Typography>
              )}
            </div>
            {renderGroupSearch && renderGroupSearch}
          </>
        )
      }
    ];
    return (
      <TabContainer
        tabs={tabs}
        initial={this.state.activeTab}
        view={false}
        onChange={this.handleTabChange}
      />
    );
  }
}

export default withSnackbar(withStyles(styles)(SearchToolbar));
